import {
  Component,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FlexLayoutType } from '../../constants/flex-layout.type';
import { StatusType } from '../../constants/status.type';
import { StatusTextType } from '../../constants/status-text.type';
import { HearingMethodType } from '../../constants/hearing-method.type';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { NgxFileDropEntry } from 'ngx-file-drop';
import { DocumentUploadModel } from '../../models/document-upload.model';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { MaterialTypeGroup } from '../../models/material-type-group.model';
import { HearingRequestDetailsModel } from '../../models/hearing-request-details.model';
import { SaveAssociateMaterialsDialogComponent } from '../../dialogs/save-associate-materials-dialog/save-associate-materials-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { ConfirmationDialogComponent } from '../../dialogs/confirmation-dialog/confirmation-dialog.component';
import { AssociateSystemMaterialModel } from '../../models/associate-system-material.model';
@Component({
  selector: 'jsg-hearing-associate-materials-view',
  templateUrl: './hearing-associate-materials-view.component.html',
  styleUrls: ['./hearing-associate-materials-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HearingAssociateMaterialsViewComponent
  implements OnInit, OnDestroy {
  @Input() selectedTabIndex: number;
  @Input() actionFilesFromSystem: AssociateSystemMaterialModel[];
  @Input() associateMaterialsUploads: DocumentUploadModel[];
  @Input() associateMaterialsUploadInfoText: string;
  @Input() associateMaterialTypeGroup: MaterialTypeGroup[];
  @Input() actionHearing: HearingRequestDetailsModel;
  @Input() canSaveAssociateMaterials: boolean;
  @Input() associateMaterialsPendingChanges: boolean;
  @Output() cancelAssociateMaterials = new EventEmitter<{
    associateDocumentUploadsFormArray: FormArray;
    fromSystemMaterialsFormArray: FormArray;
  }>();
  @Output() tabChanged = new EventEmitter<MatTabChangeEvent>();
  @Output() selectUploadTab = new EventEmitter();
  @Output() filesSelected = new EventEmitter<NgxFileDropEntry[]>();
  @Output() cancelUpload = new EventEmitter<{
    fileIndex: number;
    docUploadsFormArray: FormArray;
  }>();
  @Output() deleteUpload = new EventEmitter<{
    fileIndex: number;
    docUploadsFormArray: FormArray;
  }>();
  @Output() openFile = new EventEmitter<DocumentUploadModel>();
  @Output() saveAssociateMaterials = new EventEmitter<{
    associateDocumentUploadsFormArray: FormArray;
    fromSystemMaterialsFormArray: FormArray;
  }>();

  public associateDocumentUploadForm: FormGroup;
  public fromSystemMaterialsForm: FormGroup;

  public FlexLayoutType = FlexLayoutType;
  public StatusType = StatusType;
  public StatusTextType = StatusTextType;
  public HearingMethodType = HearingMethodType;

  private saveAssociateMaterialsDialogSubscription: Subscription;
  private cancelAssociateMaterialsDialogSubscription: Subscription;

  get associateDocumentUploadsFormArray(): FormArray {
    return this.associateDocumentUploadForm.get(
      'associateDocumentUploads'
    ) as FormArray;
  }

  get fromSystemMaterialFormAray(): FormArray {
    return this.fromSystemMaterialsForm.get('fromSystemMaterials') as FormArray;
  }

  constructor(private dialog: MatDialog, private fb: FormBuilder) {}

  ngOnInit() {
    this.associateDocumentUploadForm = this.fb.group({
      associateDocumentUploads: this.fb.array([]),
    });
    this.fromSystemMaterialsForm = this.fb.group({
      fromSystemMaterials: this.fb.array([]),
    });
  }

  public onCancelAssociateMaterials = () => {
    if (this.associateMaterialsPendingChanges) {
      this.cancelAssociateMaterialsDialogSubscription = this.dialog
        .open(ConfirmationDialogComponent, {
          autoFocus: false,
          disableClose: true,
          width: '600px',
          data: {
            caption: 'Do you want to leave without saving?',
            subcaption:
              'If you leave this page, any unsaved work will be removed and you have to associate the materials again',
            confirmButton: 'Yes, leave',
            cancelButton: 'No, stay',
          },
        })
        .afterClosed()
        .subscribe((isConfirmed: boolean) => {
          if (!isConfirmed) {
            return;
          }
          this.cancelAssociateMaterials.emit({
            associateDocumentUploadsFormArray: this
              .associateDocumentUploadsFormArray,
            fromSystemMaterialsFormArray: this.fromSystemMaterialFormAray,
          });
        });
    } else {
      this.cancelAssociateMaterials.emit({
        associateDocumentUploadsFormArray: this
          .associateDocumentUploadsFormArray,
        fromSystemMaterialsFormArray: this.fromSystemMaterialFormAray,
      });
    }
  };

  public onTabChanged = (event: MatTabChangeEvent) => {
    this.tabChanged.emit(event);
  };

  public onSelectUploadTab = () => {
    this.selectUploadTab.emit();
  };

  public onFilesSelected = (files: NgxFileDropEntry[]) => {
    this.filesSelected.emit(files);
  };

  public onCancelUploadClick = (args: {
    fileIndex: number;
    docUploadsFormArray: FormArray;
  }) => {
    this.cancelUpload.emit(args);
  };

  public onDeleteUploadClick = (args: {
    fileIndex: number;
    docUploadsFormArray: FormArray;
  }) => {
    this.deleteUpload.emit(args);
  };

  public onOpenFile = (docToOpen: DocumentUploadModel) => {
    this.openFile.emit(docToOpen);
  };

  public onSaveAssociateMaterials = () => {
    this.saveAssociateMaterialsDialogSubscription = this.dialog
      .open(SaveAssociateMaterialsDialogComponent, {
        autoFocus: false,
        disableClose: true,
        width: '700px',
      })
      .afterClosed()
      .subscribe((args: { acknowledged: boolean }) => {
        if (args.acknowledged) {
          if (this.canSaveAssociateMaterials) {
            this.saveAssociateMaterials.emit({
              associateDocumentUploadsFormArray: this
                .associateDocumentUploadsFormArray,
              fromSystemMaterialsFormArray: this.fromSystemMaterialFormAray,
            });
          }
        }
      });
  };

  ngOnDestroy(): void {
    this.saveAssociateMaterialsDialogSubscription.unsubscribe();
    this.cancelAssociateMaterialsDialogSubscription.unsubscribe();
  }
}
